import type { JSX } from 'react';
import { useCallback, useContext, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useApolloClient } from '@apollo/client';
import { useAccountAdobeManagedDetailsFragment } from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useActiveAccountId } from '@frameio/shared/contexts/ActiveEntitiesContext';
import { AppSheetContext } from '@frameio/shared/contexts/AppSheetContext';
import { useCurrentPlanTracking } from '@frameio/shared/plans/hooks/useCurrentPlanTracking';
import { getLinkToProjectWithMemberDialogOpen } from '@frameio/shared/routing/getLinkToProjectWithMembersDialogOpen';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { removeQueryParams } from '@frameio/utils/url/removeQueryParams/removeQueryParams';
import type { ToastAlertProps } from '@frameio/vapor';
import { Button2, noop, Text, ToastAlert, useToast } from '@frameio/vapor';
import { useEvictedAccessRequests } from '../contexts/EvictedAccessRequestsContext';
import type { AccessRequestNodeFragment } from '../gql/fragments/generated/AccessRequestNode';
import { AccessRequestNode } from '../gql/fragments/generated/AccessRequestNode';
import { useApproveAccessRequestMutation } from '../gql/mutations/generated/ApproveAccessRequest';
import { useDeclineAccessRequestMutation } from '../gql/mutations/generated/DeclineAccessRequest';
import { useSubmitAccessRequestMutation } from '../gql/mutations/generated/SubmitAccessRequest';
import type { AccessRequestActions } from '../types';
import TEXT from './generated/TEXT/AccessRequestActionsConfirmation';

const TOAST_DURATION = 5000;

TEXT.DEFINE({
  /** Confirmation for approval success */
  approveSuccess: (requesterUser: string, grantedProject: string): string =>
    `${requesterUser} was added to ${grantedProject}`,
  /** Confirmation for decline success */
  declineSuccess: (requesterUser: string, grantedProject: string) =>
    `You declined ${requesterUser} access to ${grantedProject}`,
  /** CTA to view the members of the project */
  successToastCta: 'View',
  /** CTA to undo the decline */
  undoDeclineCta: 'Undo',
});

export function useAccessRequestsActions({
  id,
  submissionInput,
  requesterUser,
  requestedResource,
  onSubmitComplete = noop,
  grantedProject,
  hasAccountUser,
  hasMemberLicense,
}: AccessRequestActions) {
  const [submitAccessRequest] = useSubmitAccessRequestMutation();
  const [declineAccessRequest] = useDeclineAccessRequestMutation();
  const [approveAccessRequest] = useApproveAccessRequestMutation();
  const { addToast } = useToast();
  const { setId: setSheetId } = useContext(AppSheetContext);
  const { addEvictedAccessRequest, removeEvictedAccessRequest } =
    useEvictedAccessRequests();
  const client = useApolloClient();
  const declineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const evictedItemRef = useRef<AccessRequestNodeFragment | null>(null);
  const track = useTrackUnsafe();
  const currentPlan = useCurrentPlanTracking();
  const accountId = useActiveAccountId();

  const textDeniedSuccess = TEXT.declineSuccess(
    requesterUser?.name ?? '',
    grantedProject?.name ?? ''
  ) as string;
  const textApproveSuccess = TEXT.approveSuccess(
    requesterUser?.name ?? '',
    grantedProject?.name ?? ''
  ) as string;
  const textSuccessToastCta = TEXT.successToastCta;
  const textUndoDeclineCta = TEXT.undoDeclineCta;

  let source;

  switch (requestedResource?.__typename) {
    case 'FolderAsset':
      source = 'Folder';
      break;
    case 'Collection':
      source = 'Collection';
      break;
    case 'Project':
      source = 'Project';
      break;
    default:
      source = 'Asset';
      break;
  }

  const { data: adobeAccountData } = useAccountAdobeManagedDetailsFragment({
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const isAdobeManagedAccount = isAccountAdobeManaged(
    adobeAccountData?.managedBy
  );
  const adobeOrgId = adobeAccountData?.adobeOrg ?? null;

  const accessRequestTrackingProps = useMemo(
    () => ({
      access_request_id: id,
      new_seat: !hasMemberLicense,
      is_internal_user: hasAccountUser,
      is_adobe_managed_account: isAdobeManagedAccount,
      ...(adobeOrgId && {
        adobe_org_id: adobeOrgId,
      }),
      source,
      current_plan_tier: currentPlan?.current_plan_tier,
      ...(source === 'Asset' && { asset_id: requestedResource?.id }),
      ...(source === 'Folder' && { folder_id: requestedResource?.id }),
      ...(source === 'Project' && { project_id: requestedResource?.id }),
      ...(source === 'Collection' && { collection_id: requestedResource?.id }),
    }),
    [
      id,
      hasAccountUser,
      isAdobeManagedAccount,
      adobeOrgId,
      source,
      currentPlan?.current_plan_tier,
      requestedResource,
      hasMemberLicense,
    ]
  );

  const displayToast = useCallback(
    ({
      title,
      details,
      variant,
      adornmentAfter,
    }: {
      title: ToastAlertProps['title'];
      details?: ToastAlertProps['details'];
      variant: ToastAlertProps['variant'];
      adornmentAfter?: (close?: () => void) => JSX.Element;
    }) => {
      addToast(
        (close, toastCloseLabel) => (
          <ToastAlert
            adornmentAfter={adornmentAfter ? adornmentAfter(close) : undefined}
            closeLabel={toastCloseLabel}
            details={details}
            onRequestClose={close}
            title={title}
            variant={variant}
          />
        ),
        {
          duration: TOAST_DURATION,
        }
      );
    },
    [addToast]
  );

  const handleSubmit = useCallback(async () => {
    if (!submissionInput) return;

    await submitAccessRequest({
      variables: {
        input: {
          ...submissionInput,
        },
      },
      errorPolicy: 'all',
      optimisticResponse: {
        submitAccessRequest: {
          accessRequest: {
            id: null,
          },
        },
      },
      onCompleted: () => {
        onSubmitComplete();
      },
      onError: (error) => {
        displayToast({
          title: 'Error',
          details: error.message,
          variant: 'error',
        });
      },
    });
  }, [displayToast, onSubmitComplete, submitAccessRequest, submissionInput]);

  const handleApprove = async () => {
    if (!id) return;

    await approveAccessRequest({
      variables: {
        input: {
          id,
        },
      },
      optimisticResponse: {
        approveAccessRequest: {
          accessRequest: {
            __typename: `AccessRequest`,
            id,
          },
        },
      },
      refetchQueries: ['GetAccountUsage'],

      update: (cache, { data }) => {
        const accessRequestId = data?.approveAccessRequest?.accessRequest?.id;
        if (!accessRequestId) return;

        cache.evict({
          id: cache.identify({
            __typename: 'AccessRequest',
            id: accessRequestId,
          }),
        });

        /** Update the account cache to decrement the access requests total count
         * This is only necessary for approved requests because declined requests
         * are handled through the evicted access requests context.
         */
        cache.modify({
          id: cache.identify({
            __typename: 'Account',
            id: accountId,
          }),
          fields: {
            accessRequests(existingAccessRequests, { readField }) {
              if (!existingAccessRequests) return existingAccessRequests;

              const currentTotalCount = readField(
                'totalCount',
                existingAccessRequests
              );
              if (typeof currentTotalCount !== 'number')
                return existingAccessRequests;

              return {
                ...existingAccessRequests,
                totalCount: currentTotalCount - 1,
              };
            },
          },
        });
      },
      onCompleted: () => {
        if (
          typeof requesterUser?.name !== 'string' ||
          typeof grantedProject?.name !== 'string'
        )
          return;

        displayToast({
          title: textApproveSuccess,
          variant: 'success',
          adornmentAfter: (close?: () => void) => {
            const link = getLinkToProjectWithMemberDialogOpen({
              projectId: grantedProject.id,
            });
            return (
              <Link
                href={link}
                onClick={() => {
                  close?.();
                  setSheetId?.(null);
                }}
              >
                <Text variant="link">{textSuccessToastCta}</Text>
              </Link>
            );
          },
        });
        track('access-request-responded', {
          result: 'Approved',
          ...accessRequestTrackingProps,
        });
        removeQueryParams(['ar']);
      },
      onError: (error) => {
        displayToast({
          title: 'Error',
          details: error.message,
          variant: 'error',
        });
      },
    });
  };

  /**
   * Undo the optimistic dismiss by writing the evicted item back to the cache */
  const undoOptimisticDecline = useCallback(() => {
    if (!evictedItemRef.current?.id || !id) return;

    const { cache } = client;

    cache.writeFragment({
      id: cache.identify({
        __typename: 'AccessRequest',
        id: evictedItemRef.current.id,
      }),
      fragment: AccessRequestNode,
      data: evictedItemRef.current,
    });
    /** Clear the timer that dismisses the access request and the evicted item ref */
    if (declineTimerRef.current) clearTimeout(declineTimerRef.current);
    declineTimerRef.current = null;

    /** Remove the evicted item from shared state */
    removeEvictedAccessRequest(id);
    evictedItemRef.current = null;
  }, [client, removeEvictedAccessRequest, id]);

  const handleDecline = useCallback(async () => {
    if (!id) return;

    await declineAccessRequest({
      variables: {
        input: {
          id,
        },
      },
      onCompleted: () => {
        track('access-request-responded', {
          result: 'Denied',
          ...accessRequestTrackingProps,
        });
        removeEvictedAccessRequest(id);
        if (declineTimerRef.current) {
          clearTimeout(declineTimerRef.current);
          declineTimerRef.current = null;
        }
        removeQueryParams(['ar']);
      },

      onError: (error) => {
        displayToast({
          title: 'Error',
          details: error.message,
          variant: 'error',
        });
      },
    });
  }, [
    declineAccessRequest,
    id,
    displayToast,
    accessRequestTrackingProps,
    track,
    removeEvictedAccessRequest,
  ]);

  /**
   * Optimistically declines an access request with undo functionality.
   *
   * This function removes the access request from the Apollo cache immediately
   * (optimistic UI), displays a toast with an undo button, and schedules the
   * actual decline mutation to run after a timeout. The user can undo the
   * action before the timeout expires.
   *
   * The evicted request is tracked in the EvictedAccessRequestsContext to
   * ensure it gets properly dismissed if the user navigates away before the
   * timeout expires.
   *
   * Flow:
   * 1. Reads the access request from cache and stores it as a ref
   * 2. Adds it to the evicted items list along with its decline handler
   * 3. Evicts it from the cache (UI updates immediately)
   * 4. Shows a toast notification with an undo button
   * 5. Schedules the actual decline mutation after TOAST_DURATION
   */
  const optimisticallyDeclineRequest = useCallback(async () => {
    const { cache } = client;
    const accessRequestId = cache.identify({ __typename: 'AccessRequest', id });

    if (!evictedItemRef.current) {
      /** Store the current item as a ref before evicting */
      evictedItemRef.current = cache.readFragment<AccessRequestNodeFragment>({
        id: accessRequestId,
        fragment: AccessRequestNode,
      });

      if (evictedItemRef.current)
        addEvictedAccessRequest(evictedItemRef.current, handleDecline);

      cache.evict({ id: accessRequestId });

      displayToast({
        title: textDeniedSuccess,
        variant: 'info',
        adornmentAfter: (close?: () => void) => (
          <Button2
            onPress={() => {
              close?.();
              undoOptimisticDecline();
            }}
            size="small"
            variant="tertiary"
          >
            <Text variant="link">{textUndoDeclineCta}</Text>
          </Button2>
        ),
      });

      declineTimerRef.current = setTimeout(() => {
        handleDecline();
      }, TOAST_DURATION);
    }
  }, [
    client,
    id,
    displayToast,
    undoOptimisticDecline,
    handleDecline,
    addEvictedAccessRequest,
    textDeniedSuccess,
    textUndoDeclineCta,
  ]);

  return {
    handleSubmit,
    handleApprove,
    handleDecline,
    optimisticallyDeclineRequest,
  };
}
